"use strict";
/**
 * AI Provider Types - Pluggable AI Service Architecture
 *
 * This module defines the schema for dynamically configurable AI providers.
 * Super Admin can add/remove/configure providers without code changes.
 *
 * KEY DESIGN PRINCIPLES:
 * 1. Capability-based: Providers declare what they can do
 * 2. Pipeline support: Chain multiple capabilities (generate → remove bg)
 * 3. Skip optimization: If provider handles multiple capabilities, skip steps
 * 4. Dynamic fallback: Automatic failover per capability
 * 5. Zero code changes: Add new providers via Super Admin UI
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_PIPELINES = void 0;
exports.getProvidersByCapability = getProvidersByCapability;
exports.getDefaultProvider = getDefaultProvider;
/**
 * Default pipelines for common operations
 */
exports.DEFAULT_PIPELINES = [
    {
        id: 'menu_image_generation',
        name: 'Menu Image Generation',
        description: 'Generate product images with transparent backgrounds for menus',
        steps: [
            {
                capability: 'transparent_generation',
                required: false,
                skipIf: [] // If this succeeds, skip background_removal
            },
            {
                capability: 'image_generation',
                required: true,
                skipIf: ['transparent_generation'] // Skip if transparent gen worked
            },
            {
                capability: 'background_removal',
                required: false,
                skipIf: ['transparent_generation'] // Skip if provider already did transparent
            }
        ],
        enabled: true
    },
    {
        id: 'image_enhancement',
        name: 'Enhance Existing Image',
        description: 'Analyze existing image and generate enhanced version',
        steps: [
            { capability: 'image_analysis', required: true, skipIf: [] },
            { capability: 'image_generation', required: true, skipIf: [] },
            { capability: 'background_removal', required: false, skipIf: ['transparent_generation'] }
        ],
        enabled: true
    }
];
/**
 * DEFAULT_PROVIDERS (the seed of the retired `ai-providers` table and its raw
 * vendor endpoint templates) was removed on 2026-10-05 (Ruby Generation Engine
 * Task 27). Providers, models and their credentials now live in the AI
 * registry (PROVIDER#/MODEL# rows) and every call goes through @platform/ai-router.
 */
/**
 * Helper to get providers by capability
 */
function getProvidersByCapability(providers, capability) {
    return providers
        .filter(p => p.enabled && p.status === 'active' && p.capabilities.includes(capability))
        .sort((a, b) => a.priority - b.priority);
}
/**
 * Helper to get the default provider for a capability
 */
function getDefaultProvider(providers, capability) {
    const capable = getProvidersByCapability(providers, capability);
    return capable.find(p => p.isDefault) || capable[0] || null;
}
//# sourceMappingURL=aiProviders.js.map